import axios, {
	type AxiosError,
	type AxiosInstance,
	type AxiosResponse,
} from "axios";
import { toast } from "sonner";
import CaptchaTokenService from "../captcha/captchaToken.service";
import * as Sentry from "@sentry/react";
import i18n from "@/lib/i18n";

/**
 * This is the base class for all API clients. It is used to make requests to
 * the backend. It will include the base URL and the credentials.
 *
 * it's configured to only send application/json content type and to include
 * cookies by default.
 */
export class BaseApiClient {
	private static instance: BaseApiClient | undefined;

	// singleton instance
	private client: AxiosInstance;
	private authToken: string | null = null;

	/**
	 * Checks if the request has an explicit Authorization header
	 * @param config The request config
	 * @returns True if the request has an explicit Authorization header, false otherwise
	 */
	private hasExplicitAuthorizationHeader(config: unknown): boolean {
		const headers = (config as { headers?: unknown } | undefined)?.headers;
		if (!headers) return false;

		// Axios v1 can use AxiosHeaders (has .get()), or a plain object
		const maybeAxiosHeaders = headers as { get?: unknown };
		if (typeof maybeAxiosHeaders.get === "function") {
			const v = (maybeAxiosHeaders.get as (name: string) => unknown)(
				"Authorization",
			);
			return typeof v === "string" && v.length > 0;
		}

		const record = headers as Record<string, unknown>;
		const v = record["Authorization"] ?? record["authorization"];
		return typeof v === "string" && v.length > 0;
	}

	private constructor() {
		if (!import.meta.env.VITE_API_URL) {
			throw new Error("VITE_API_URL is not set");
		}
		this.client = axios.create({
			baseURL: `${import.meta.env.VITE_API_URL}/api/v1/`,
			withCredentials: true, // Always send cookies
			headers: {
				"Content-Type": "application/json",
				"Cache-Control": "no-cache, no-store, must-revalidate", // this is used to prevent caching
			},
			timeout: 20000, // 20 seconds
		});

		this.client.interceptors.request.use((config) => {
			// If the caller explicitly set Authorization (e.g. Firebase ID token),
			// do not override it with the default JWT/cookie token.
			if (this.hasExplicitAuthorizationHeader(config)) {
				return config;
			}

			// First try to use the token set directly via setAuthToken()
			let token = this.authToken;

			// Fallback to reading from cookie if no direct token is set
			if (!token) {
				token =
					document.cookie
						.split("; ")
						.find((row) => row.startsWith("jwt_token="))
						?.split("=")[1] || null;
			}

			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			} else {
				console.warn(
					`No JWT token found, request will not be authenticated. ${config.url}`,
				);
			}

			try {
				CaptchaTokenService.getInstance().attachToAxiosConfig(
					config,
					this.client.defaults.baseURL,
				);
			} catch (_) {
				console.error("Failed to attach captcha token", _);
				Sentry.captureException(new Error("Failed to attach captcha token"), {
					extra: { config },
				});
			}
			return config;
		});

		// Add response interceptor to handle token expiration and warnings
		this.client.interceptors.response.use(
			(response) => {
				// Handle token expiration warning
				if (response.headers["x-token-expiring-soon"] === "true") {
					console.log("Authentication token will expire soon");
					window.dispatchEvent(new CustomEvent("auth:refreshToken"));
				}
				return response;
			},
			async (error) => {
				// Handle expired token errors
				const isTokenExpired =
					error.response?.data?.error === "Token expired" ||
					error.response?.data === '{"error":"Token expired"}';

				if (isTokenExpired) {
					console.log("Authentication token expired, logging out user");
					window.dispatchEvent(new CustomEvent("auth:tokenExpired"));
				}

				// Surface the Creator trial rolling cap (400 coins / 4h) as a global
				// event so a single mounted listener can open the cap-hit modal,
				// regardless of which feature triggered the debit.
				const errorData = error.response?.data as
					| {
							code?: string;
							remainingCoins?: number;
							windowResetAt?: string;
					  }
					| undefined;
				if (
					error.response?.status === 429 &&
					errorData?.code === "TRIAL_RATE_LIMIT"
				) {
					window.dispatchEvent(
						new CustomEvent("trial:capHit", {
							detail: {
								remainingCoins: errorData.remainingCoins ?? 0,
								windowResetAt: errorData.windowResetAt,
							},
						}),
					);
				}

				// Handle request timeout errors explicitly
				const axiosError = error as AxiosError;
				const isTimeoutError =
					axiosError.code === "ECONNABORTED" ||
					(typeof axiosError.message === "string" &&
						axiosError.message.toLowerCase().includes("timeout"));

				if (isTimeoutError) {
					toast.error(i18n.t("network.internetGone"), {
						description: i18n.t("network.repeatAction"),
					});
					return Promise.reject(error);
				}

				return Promise.reject(error);
			},
		);
	}

	public static getInstance(): BaseApiClient {
		if (!BaseApiClient.instance) {
			BaseApiClient.instance = new BaseApiClient();
		}
		return BaseApiClient.instance;
	}

	/**
	 * Sets the authentication token directly for immediate use in requests
	 * This ensures the token is available immediately without waiting for cookie updates
	 * @param token - The JWT token to use for authentication
	 */
	public setAuthToken(token: string | null): void {
		this.authToken = token;
	}

	/**
	 * Clears the authentication token
	 */
	public clearAuthToken(): void {
		this.authToken = null;
	}

	/**
	 * Resets the BaseApiClient instance to a clean state
	 * This helps avoid issues with stale state after failed authentications
	 */
	public static resetInstance(): void {
		if (BaseApiClient.instance) {
			BaseApiClient.instance.clearAuthToken();
			// Force a new instance to be created on next getInstance() call
			BaseApiClient.instance = undefined;
			console.log("BaseApiClient instance reset completed");
		}
	}

	/**
	 * Template method for GET requests.
	 * @param endpoint
	 * @param params
	 * @returns
	 */
	public async get<T>(
		endpoint: string,
		params: Record<string, unknown> = {},
	): Promise<T> {
		const response: AxiosResponse<T> = await this.client.get(endpoint, {
			params,
		});
		return response.data;
	}

	/**
	 * Template method for POST requests.
	 * @param T The type of the response
	 * @param D The type of the data to send in the request body
	 * @param endpoint
	 * @param data
	 * @returns
	 */
	public async post<T, D>(endpoint: string, data: D): Promise<T> {
		const response: AxiosResponse<T> = await this.client.post(endpoint, data);
		return response.data;
	}

	/**
	 * POST using a caller-supplied Bearer token (e.g. Firebase ID token),
	 * without mutating global auth state.
	 */
	public async postWithBearerToken<T, D>(
		endpoint: string,
		data: D,
		bearerToken: string,
	): Promise<T> {
		const response: AxiosResponse<T> = await this.client.post(endpoint, data, {
			headers: {
				Authorization: `Bearer ${bearerToken}`,
			},
		});
		return response.data;
	}

	/**
	 * Template method for POST requests with a custom timeout
	 * @param T The type of the response
	 * @param D The type of the data to send in the request body
	 * @param endpoint
	 * @param data
	 * @param timeout Optional timeout in milliseconds for this specific request
	 * @returns
	 */
	public async postWithTimeout<T, D>(
		endpoint: string,
		data: D,
		timeout?: number,
	): Promise<T> {
		const config = timeout ? { timeout } : {};
		const response: AxiosResponse<T> = await this.client.post(
			endpoint,
			data,
			config,
		);
		return response.data;
	}

	/**
	 * Template method for PATCH requests.
	 * @param endpoint
	 * @param data
	 * @returns
	 */
	public async patch<T, D>(endpoint: string, data: D): Promise<T> {
		const response: AxiosResponse<T> = await this.client.patch(endpoint, data);
		return response.data;
	}

	/**
	 * Template method for PUT requests.
	 * @param endpoint
	 * @param data
	 * @returns
	 */
	public async put<T, D>(endpoint: string, data: D): Promise<T> {
		const response: AxiosResponse<T> = await this.client.put(endpoint, data);
		return response.data;
	}

	/**
	 * Template method for DELETE requests.
	 * @param endpoint
	 * @returns
	 */
	public async delete<T>(endpoint: string): Promise<T> {
		const response: AxiosResponse<T> = await this.client.delete(endpoint);
		return response.data;
	}

	/**
	 * Template method for file uploads.
	 * @param endpoint The API endpoint
	 * @param file The file to upload
	 * @param additionalData Additional form data to include
	 * @param options Optional configuration
	 * @param options.timeout Timeout in milliseconds (default: 180000 = 3 minutes)
	 * @param options.onUploadProgress Callback for upload progress (0-100)
	 * @returns The response data
	 */
	public async uploadFile<T>(
		endpoint: string,
		file: File,
		additionalData: Record<string, unknown> = {},
		options?: {
			timeout?: number;
			onUploadProgress?: (progress: number) => void;
		},
	): Promise<T> {
		const formData = new FormData();
		// Append additional fields BEFORE the file so fastify's request.file().fields contains them
		for (const [key, value] of Object.entries(additionalData)) {
			formData.append(key, String(value));
		}
		formData.append("file", file);

		// Create a custom config to override the default Content-Type
		const config = {
			headers: {
				"Content-Type": "multipart/form-data",
			},
			// Default to 3 minutes for file uploads (much longer than default 20s)
			timeout: options?.timeout ?? 180000,
			// Track real upload progress
			onUploadProgress: options?.onUploadProgress
				? (progressEvent: { loaded: number; total?: number }) => {
						if (progressEvent.total) {
							const percentComplete = Math.round(
								(progressEvent.loaded / progressEvent.total) * 100,
							);
							options.onUploadProgress?.(percentComplete);
						}
					}
				: undefined,
		};

		const response: AxiosResponse<T> = await this.client.post(
			endpoint,
			formData,
			config,
		);
		return response.data;
	}
}

export default BaseApiClient;
